/**
 * Tracks active network renderer connectivity when discovery list churns.
 * Pins the user's zone selection — never silently falls back to System Audio.
 */

import type { TrackMetadata } from '../protocols/BATON';
import { useMaestroStore } from '../store/maestroStore';

export type RendererConnectivityState = 'live' | 'reconnecting' | 'unreachable';

export const RENDERER_UNREACHABLE_MS = 120_000;

export type PendingPlayWhileReconnecting =
  | { kind: 'track'; track: TrackMetadata }
  | { kind: 'resume' }
  | { kind: 'queueIndex'; queueIndex: number };

export type HydrateCommand = 'play' | 'pause' | 'seek' | 'volume';

export function isRemoteRendererZone(
  zone: { id?: string; type?: string } | null | undefined,
): boolean {
  if (!zone?.id) return false;
  return (
    zone.type !== 'local'
    && zone.type !== 'browser'
    && zone.type !== 'local-native'
    && zone.id !== 'this_computer'
    && zone.id !== 'local_computer'
    && zone.id !== 'this_device'
  );
}

export function isActiveRendererInZoneList(): boolean {
  const store = useMaestroStore.getState();
  const az = store.discovery?.activeZone;
  if (!isRemoteRendererZone(az)) return true;
  const rid = store.activeConnectorRenderer?.id || az!.id;
  const zones = store.discovery?.discoveredZones || [];
  return zones.some((z) => z.id === rid);
}

/** Reconcile connectivity state after discovery list updates. */
export function syncRendererConnectivityFromZones(): void {
  const store = useMaestroStore.getState();
  const az = store.discovery?.activeZone;

  if (!isRemoteRendererZone(az)) {
    if (store.rendererConnectivityState !== 'live') {
      store.setRendererConnectivity('live', null);
    }
    return;
  }

  const zones = store.discovery?.discoveredZones || [];
  const targetId = store.activeConnectorRenderer?.id || az!.id;
  const inList = zones.some((z) => z.id === targetId);
  const now = Date.now();

  if (inList) {
    const prev = store.rendererConnectivityState;
    store.setRendererConnectivity('live', now);
    if (prev !== 'live') {
      void flushPendingPlayAfterReconnect();
    }
    return;
  }

  const lastSeen = store.rendererLastSeenAt ?? now;
  const conn = store.rendererConnectivityState;

  if (conn === 'live') {
    store.setRendererConnectivity('reconnecting', lastSeen);
  } else if (conn === 'reconnecting' && now - lastSeen >= RENDERER_UNREACHABLE_MS) {
    store.setRendererConnectivity('unreachable', lastSeen);
  }
}

/** Promote reconnecting → unreachable when grace window expires. */
export function tickRendererConnectivityTimeout(): void {
  const store = useMaestroStore.getState();
  if (store.rendererConnectivityState !== 'reconnecting') return;
  if (!isRemoteRendererZone(store.discovery?.activeZone)) return;
  const lastSeen = store.rendererLastSeenAt;
  if (!lastSeen) return;
  if (Date.now() - lastSeen >= RENDERER_UNREACHABLE_MS) {
    store.setRendererConnectivity('unreachable', lastSeen);
  }
}

export function formatDeviceLabelWithConnectivity(
  baseName: string,
  state: RendererConnectivityState,
  t: (key: string) => string,
): string {
  if (state === 'reconnecting') {
    return `${baseName} (${t('player.renderer_reconnecting')})`;
  }
  if (state === 'unreachable') {
    return `${baseName} (${t('player.renderer_unreachable')})`;
  }
  return baseName;
}

/**
 * When the active network renderer is absent from discoveredZones:
 * - live/reconnecting: preserve selection; no-op transport except queue play intent
 * - unreachable: no-op except new play (caller shows chooser)
 * Returns true if the caller should proceed with the transport command.
 */
export function guardAbsentNetworkRenderer(command: HydrateCommand): boolean {
  const store = useMaestroStore.getState();
  const hasBridge = !!(window as any).__TAURI__ || !!(window as any).__maestroNative;

  if (!hasBridge && store.activeConnectorRenderer) {
    console.log('[Connectivity] Clearing synced renderer on bridgeless platform:', store.activeConnectorRenderer.id);
    store.setActiveConnectorRenderer(null);
    return command !== 'play';
  }

  const zones = store.discovery?.discoveredZones || [];
  const renderer = store.activeConnectorRenderer;
  const az = store.discovery?.activeZone;
  const remoteActive = isRemoteRendererZone(az) || (renderer && renderer.id !== 'this_device');

  if (!remoteActive) {
    return true;
  }

  const targetId = renderer?.id || az?.id;
  const missing = zones.length > 0 && targetId && !zones.some((z) => z.id === targetId);

  if (!missing) {
    return true;
  }

  syncRendererConnectivityFromZones();
  const conn = store.rendererConnectivityState;

  if (conn === 'live' || conn === 'reconnecting') {
    if (command === 'play') {
      store.setPendingPlayWhileReconnecting({ kind: 'resume' });
      console.log('[Connectivity] Play queued while reconnecting to', az?.name || targetId);
    } else {
      console.log('[Connectivity] Transport no-op while reconnecting:', command);
    }
    return false;
  }

  if (conn === 'unreachable') {
    if (command !== 'play') {
      console.log('[Connectivity] Transport no-op while unreachable:', command);
      return false;
    }
    return false;
  }

  return true;
}

export async function flushPendingPlayAfterReconnect(): Promise<void> {
  const store = useMaestroStore.getState();
  const pending = store.pendingPlayWhileReconnecting;
  if (!pending) return;
  store.setPendingPlayWhileReconnecting(null);

  const { QueueController } = await import('./QueueController');
  if (pending.kind === 'track') {
    await QueueController.playSingle(pending.track);
  } else if (pending.kind === 'queueIndex') {
    await QueueController.playQueueTrack(pending.queueIndex);
  } else if (pending.kind === 'resume') {
    const { TransportCommand } = await import('./TransportCommand');
    await TransportCommand.play();
  }
}

/** Build pinned zone row for device picker when active renderer is missing from list. */
export function getPinnedActiveNetworkZone() {
  const store = useMaestroStore.getState();
  const az = store.discovery?.activeZone;
  if (!isRemoteRendererZone(az)) return null;
  const zones = store.discovery?.discoveredZones || [];
  if (zones.some((z) => z.id === az!.id)) return null;
  return az;
}
